import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { AppSidebar } from './AppSidebar';
import { Footer } from './Footer';
import { IntroVideo } from '@/components/shared/IntroVideo';
import { shouldShowIntroToday, markIntroShown } from '@/components/shared/introVideoUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useRegisterOfficeUser } from '@/features/auth/hooks/useRegisterOfficeUser';
import { useIsMobile } from '@/hooks/useMediaQuery';

export function Layout() {
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();
  useRegisterOfficeUser();
  // Show intro video once per day
  const [showIntro, setShowIntro] = useState(() => shouldShowIntroToday());

  const handleIntroComplete = () => {
    markIntroShown();
    setShowIntro(false);
  };

  // signOut clears the session tenant and does a hard reload to /login.
  const handleLogout = signOut;

  const userInfo = user
    ? { email: user.email, displayName: user.displayName, photoURL: user.photoURL }
    : undefined;

  // Mobile keeps the existing top header + drawer; desktop gets the calm-v2
  // right-side sidebar shell (recovers vertical space, all nav groups visible).
  if (isMobile) {
    return (
      <>
        {showIntro && <IntroVideo onComplete={handleIntroComplete} />}
        <div className="app-bg flex flex-col">
          <Header user={userInfo} onLogout={handleLogout} />
          <main className="flex-1 p-4">
            <Outlet />
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Intro video overlay - app loads underneath */}
      {showIntro && <IntroVideo onComplete={handleIntroComplete} />}

      {/* Plain flex row: under dir="rtl" the first child (sidebar) docks on the
          physical RIGHT; row-reverse double-flips it to the left. */}
      <div className="app-bg flex min-h-screen">
        <AppSidebar user={userInfo} onLogout={handleLogout} />
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="flex-1 p-6 lg:p-8">
            <div className="mx-auto max-w-7xl">
              <Outlet />
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </>
  );
}
